import { RunContextApp } from "framework/RCApp";
import { ErrorUtils } from "framework/utility/ErrorUtils";
import { AxiosInstance } from "axios";
import { SaveProjectConfigData } from "types/apis/pilotInterfaces";
import axios from "axios";


export interface BffApiResponse<T> {
  data?: T;
  error?: any;
}

export class BffApiClient {
  private client !: any
  private axiosClient: AxiosInstance

  constructor(private rc: RunContextApp, private toolUrl: string) {
    this.axiosClient = axios.create()
    this.init()
  }

  init() {
    // Set up request interceptor to automatically add authorization header
    this.axiosClient.interceptors.request.use(request => {
      const authToken = this.rc.session.getCMSAccessToken();
      if (authToken) {
        request.headers.Authorization = `Bearer ${authToken}`;
      }
      return request;
    });

    // Set up response interceptor for error handling
    this.axiosClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  initWithJwt(jwt: string) {
    this.axiosClient.defaults.headers.common['Authorization'] = `Bearer ${jwt}`
  }

  async login(id: string, password: string){
    try {
      const loginResp = await this.axiosClient.post(`${this.toolUrl}/v1/login`, {
        email: id,
        password: password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return loginResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/login\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getProjects() {
    try {
      const projectsResp = await this.axiosClient.get(`${this.toolUrl}/v1/projects`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return projectsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getStrapiProjects() {
    try {
      const strapiProjectsResp = await this.axiosClient.get(`${this.toolUrl}/v1/strapi-projects`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return strapiProjectsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/strapi-projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getUtilities() {
    try {
      const utilitiesResp = await this.axiosClient.get(`${this.toolUrl}/v1/utilities`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return utilitiesResp

    } catch (error) { 
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/utilities\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createUtility(utilityData: any) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/utilities`, utilityData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/utilities\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createEnvironment(projectId: string, environmentData: { utilityName: string; environmentName: string }) {
    
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/create-env/${projectId}`, environmentData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/create-env\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getEnvironments(utilityId: number) {
    try {
      const environmentsResp = await this.axiosClient.post(`${this.toolUrl}/v1/environments`,
        {
          utility_id: utilityId
        }
        , {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return environmentsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/environments/${utilityId}\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getAllEnvironments() {
    try {
      const allEnvironmentsResp = await this.axiosClient.get(`${this.toolUrl}/v1/all-environments`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return allEnvironmentsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/all-environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getAssociatedEnvironments(projectId: string) {
    try {
      const associatedEnvironmentsResp = await this.axiosClient.get(`${this.toolUrl}/v1/projects/${projectId}/associated-environments`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return associatedEnvironmentsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/projects/${projectId}/associated-environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async associateEnvironment(projectId: string, environmentId: string) {
    try {
      const associateEnvironmentResp = await this.axiosClient.post(`${this.toolUrl}/v1/projects/${projectId}/associated-environments`, {
        environment_id: environmentId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return associateEnvironmentResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/projects/${projectId}/associated-environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }


  async checkProcessStatus(projectId: string,processId: string) {
    try {
      
      const statusResp = await this.axiosClient.get(`${this.toolUrl}/v1/check-status/${projectId}/${processId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return statusResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/check-status/<process_id>` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createProject(projectData: any) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/projects`, projectData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createProjectAndPushConfigs(projectId: string) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/project-management/create-project-and-push-configs`, {
        project_id: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/project-management/create-project-and-push-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createRecoProject(projectId: string, sourcePilotId: string) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/projects/reco`, {
        projectId,
        sourcePilotId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/projects/reco\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async updateProject(projectId: string, projectData: any) {
    try {
      // Include projectId in the payload instead of URL
      const payloadWithProjectId = {
        ...projectData,
        projectId: projectId
      };
      
      const updateResp = await this.axiosClient.patch(`${this.toolUrl}/v1/projects`, payloadWithProjectId, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return updateResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> PATCH request to : /v1/projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getAnswer(requestData: { question: string; user_id: string; context_id?: string }) {
    try {
      const answerResp = await this.axiosClient.post(`${this.toolUrl}/chat`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return answerResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /answer\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getPilotConfigs(pilotId: string){
    try {
      const pilotConfigsResp = await this.axiosClient.get(`${this.toolUrl}/v1/pilot-configs`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: {
          pilotId: pilotId
        }
      })

      return pilotConfigsResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/pilot-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getPilotConfigsWithFilters(projectId: string, apiHost: string, application?: string, fuelType?: string) {
    try {
      const requestData: any = {
        project_id: projectId,
        api_host: apiHost
      };

      if (application && application !== 'ALL') {
        requestData.module_name = application;
      }

      if (fuelType && fuelType !== 'All') {
        requestData.fuel_type = fuelType;
      }

      const pilotConfigsResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-pilot-configs`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return pilotConfigsResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/get-pilot-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getIngestionConfigs(pilotId: string){
    try {
      const ingestionConfigsResp = await this.axiosClient.get(`${this.toolUrl}/v1/ingestion_configs/${pilotId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return ingestionConfigsResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/ingestion_configs/${pilotId}\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async saveProjectConfigs(configsData: SaveProjectConfigData[]){
    try {
      const saveConfigResp = await this.axiosClient.post(`${this.toolUrl}/v1/save_project_config`, configsData, {
        headers: {
          'Content-Type': 'application/json',
        }
      })

      return saveConfigResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/save_project_config\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  // Keep the old method for backward compatibility
  async saveProjectConfig(configData: SaveProjectConfigData){
    return this.saveProjectConfigs([configData]);
  }

  async getConfigRegistry(){
    try {
      const configRegistryResp = await this.axiosClient.get(`${this.toolUrl}/v1/get_config_registry`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return configRegistryResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/get_config_registry\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getApplicationsMetadata(api_host: string, pilot_id: string) {
    try {
      
      const applicationsMetadataResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-project-application-metadata`, {
        api_host: api_host,
        project_id: pilot_id
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return applicationsMetadataResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : ${this.toolUrl}/v1/get-project-application-metadata` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getProjectAttributes(project_id: string) {
    try {
      
      const projectAttributesResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-project-attributes`, {
        project_id: project_id
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return projectAttributesResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : ${this.toolUrl}/v1/get-project-attributes` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getClusterConfigs(cluster_id: string, projectId: string) {
    try {
      const clusterConfigResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-cluster-config`, {
        cluster_id: cluster_id,
        project_id: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return clusterConfigResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /get-cluster-config\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }




  async getClusters(project_id: string) {
    try {
      
      const clustersResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-pilot-clusters`, {
        project_id: project_id
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return clustersResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : ${this.toolUrl}/v1/get-clusters\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createConfigTypes(api_host: string, configData: any) {
    try {
      // Create a separate axios instance for this request to bypass automatic token handling
      const customAxiosClient = axios.create();
      
      const createConfigResp = await customAxiosClient.post(`https://${api_host}/config_types`, configData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createConfigResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : https://${api_host}/config_types\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async predictConfigs(formData: any) {
    try {
      const predictConfigsResp = await this.axiosClient.post(`http://localhost:5000/predict-configs`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return predictConfigsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : http://localhost:5000/predict-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async pushConfigsToUat(projectId: string, sourceName?: string) {
    try {
      const pushConfigsToUatResp = await this.axiosClient.post(`${this.toolUrl}/v1/push-configs-to-dataserver`, {
        project_id: projectId,
        source_name:sourceName
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return pushConfigsToUatResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/push-configs-to-dataserver\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getPendingUatConfigs(projectId: string) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/projects/${projectId}/pending-uat-configs`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/projects/${projectId}/pending-uat-configs\n` +
        `\nresulted in error : \n`, error);
      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }
  
  // Data Generator API Methods
  async fetchUserDetails(requestData: {
    userUUID: string[];
    utilityId?: number; // Made optional since backend will assign it
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    houseType?: string;
    subPilot?: string;
    ingestionBucket?: string;
    fileUploadBucket: string;
    meterFuel?: string[];
    destinationPilotId?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/fetchProdUserData/userDetails`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error: any) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/fetchProdUserData/userDetails\n` + 
        `\nresulted in error : \n`, error)

      if (error?.response?.data?.errorDetails) {
        return { error: error.response.data.errorDetails };
      } else if (error?.response?.data?.message) {
        return { error: error.response.data.message };
      }

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  // New GET method for userDetails
  async getUserDetails(requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userUUID: requestData.userUUID,
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        sourceUsername: requestData.sourceUsername,
        sourcePassword: requestData.sourcePassword
      });

      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      if (requestData.destinationEnv) {
        queryParams.append('destinationEnv', requestData.destinationEnv);
      }

      if (requestData.meterFuel) {
        queryParams.append('meterFuel', requestData.meterFuel);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/userDetails?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/userDetails\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchRawData(requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userUUID: requestData.userUUID,
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        sourceUsername: requestData.sourceUsername,
        sourcePassword: requestData.sourcePassword
      });

      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      if (requestData.destinationEnv) {
        queryParams.append('destinationEnv', requestData.destinationEnv);
      }

      if (requestData.meterFuel) {
        queryParams.append('meterFuel', requestData.meterFuel);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/rawData?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/rawData\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchBillingData(requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
    hasBBUserProgram?: boolean;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userUUID: requestData.userUUID,
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        sourceUsername: requestData.sourceUsername,
        sourcePassword: requestData.sourcePassword
      });

      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      if (requestData.destinationEnv) {
        queryParams.append('destinationEnv', requestData.destinationEnv);
      }

      if (requestData.meterFuel) {
        queryParams.append('meterFuel', requestData.meterFuel);
      }

      if (requestData.hasBBUserProgram !== undefined) {
        queryParams.append('hasBBUserProgram', requestData.hasBBUserProgram.toString());
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/billingData?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/billingData\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchMeterData(requestData: {
    userUUID: string[];
    utilityId?: number; // Made optional since backend will assign it
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    houseType?: string;
    subPilot?: string;
    ingestionBucket?: string;
    fileUploadBucket: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/fetchProdUserData/meterData`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/fetchProdUserData/meterData\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async saveUserMetadata(requestData: {
    first_name: string;
    last_name?: string;
    rate_plan: string;
    billing_cycle?: string;
    bb_duration?: string;
    primary_email: string;
    secondary_email?: string;
    address: string;
    meter_type?: string;
    dcx_clusters?: string;
    zipcode?: string;
    persona_name?: string;
    persona_description?: string;
    source_uuid: string;
    source_pilot_id?: string;
    destination_pilot_id?: string;
    source_env: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/user-metadata`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error: any) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/user-metadata\n` + 
        `\nresulted in error : \n`, error)

      if (error?.response?.data?.message) {
        return {
          error: error.response.data.message
        }
      }

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async deleteUserMetadata(recordId: number) {
    try {
      const response = await this.axiosClient.delete(`${this.toolUrl}/v1/user-metadata/${recordId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> DELETE request to : /v1/user-metadata/${recordId}\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getUserMetadata(sourceUuid: string, sourceEnv: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/user-metadata`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: {
          source_uuid: sourceUuid,
          source_env: sourceEnv
        }
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/user-metadata\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  // New method to fetch all user metadata without query parameters
  async getAllUserMetadata(destinationPilotId?: string, status?: string) {
    try {
      let url = `${this.toolUrl}/v1/user-metadata`;
      const params = new URLSearchParams();
      
      if (destinationPilotId) {
        params.append('destination_pilot_id', destinationPilotId);
      }
      
      if (status) {
        params.append('status', status);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await this.axiosClient.get(url);
      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/user-metadata\n` +
        `\nresulted in error : \n`, error)
      return { error: ErrorUtils.getApiErrorInfo(error) }
    }
  }

  async getProjectLocales(projectId: string) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/projects/${projectId}/locales`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      return resp
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/projects/${projectId}/locales\n` +
        `\nresulted in error : \n`, error)
      return { error: ErrorUtils.getApiErrorInfo(error) }
    }
  }

  async fetchRatePlans(apiHost: string | undefined, utilityId: string) {
    try {
      const params: { api_host?: string; utility_id: string } = {
        utility_id: utilityId
      };
      
      if (apiHost) {
        params.api_host = apiHost;
      }
      
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetch-rates-plans`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetch-rates-plans\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createBillingCycleSchedule(requestData: {
    destinationPilotId: string;
    destinationEnv: string;
    sourceUUID: string;
    sourceEnv: string;
    utilityName: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destinationPilotId: requestData.destinationPilotId,
        destinationEnv: requestData.destinationEnv,
        sourceUUID: requestData.sourceUUID,
        sourceEnv: requestData.sourceEnv,
        utilityName: requestData.utilityName
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/createBCSchedule?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/createBCSchedule\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchUserUUID(requestData: {
    destinationPilotId: string;
    destinationEnv: string;
    sourceEnv: string;
    sourceUUID: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destinationPilotId: requestData.destinationPilotId,
        destinationEnv: requestData.destinationEnv,
        sourceEnv: requestData.sourceEnv,
        sourceUUID: requestData.sourceUUID
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/fetchUserUUID?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /fetchUserUUID\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async assignShcCluster(requestData: {
    uuid: string;
    data_server_url?: string;
    destination_pilot_id: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/assignShcCluster`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

     
      return {
        data: response.data,
        status: response.status
      }
    } catch (error: any) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/assignShcCluster\n` + 
        `\nresulted in error : \n`, error)

      const errorInfo = ErrorUtils.getApiErrorInfo(error);
      const errorResponseData = error?.response?.data || {};
      
      return {
        error: errorInfo,
        data: errorResponseData
      }
    }
  }

  // Generate web preview URL for user dashboard
  async generateUserWebDashboard(requestData: {
    uuid: string;
    destination_pilot_id?: string;
    api_host?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/user-web-dashboard`, requestData);
      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/user-web-dashboard\n` +
        `\nresulted in error : \n`, error)
      return { error: ErrorUtils.getApiErrorInfo(error) }
    }
  }

  // Get application scope data based on user attributes with optional prefill support
  async getApplicationScope(queryParams: {
    project_id?: string;
  }) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/application-scope`, {
        params: queryParams,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/application-scope\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Get DA Application Master data with filtering and pagination
  async getDAApplicationMaster(queryParams: any = {}) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/da-application-master`, {
        params: queryParams,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/da-application-master\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Save application scope data
  async saveApplicationScope(scopeData: { project_id: string; applications: any[] }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/application-scope/save`, scopeData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/application-scope/save\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Get DA Pipeline Master data with filtering and pagination
  async getDAPipelineMaster(queryParams: any = {}) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/pipeline-master`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/pipeline-master\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Get Pipeline Components data
  async getPipelineComponents(projectId: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/pipeline`, {
        params: {
          projectId: projectId
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/pipeline\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Call Hybrid Models API
  async callHybridModels(projectId: string, v1Fuels: string[], v2Fuels: string[]) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/hybrid-models`, {
        projectId: projectId,
        v1_fuels: v1Fuels,
        v2_fuels: v2Fuels
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/hybrid-models\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Fetch rate transition history for a user
  async fetchRateHistory(requestData: {
    uuid?: string;
    utility_name?: string;
    source_uuid?: string;
    source_env?: string;
    destination_pilot_id?: string;
    source_pilot_id?: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      
      if (requestData.uuid) {
        queryParams.append('uuid', requestData.uuid);
      }
      if (requestData.utility_name) {
        queryParams.append('utility_name', requestData.utility_name);
      }
      if (requestData.source_uuid) {
        queryParams.append('source_uuid', requestData.source_uuid);
      }
      if (requestData.source_env) {
        queryParams.append('source_env', requestData.source_env);
      }
      if (requestData.destination_pilot_id) {
        queryParams.append('destination_pilot_id', requestData.destination_pilot_id);
      }
      if (requestData.source_pilot_id) {
        queryParams.append('source_pilot_id', requestData.source_pilot_id);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetch-rate-history?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/fetch-rate-history\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async archiveProject(projectId: string) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/archive-project`, {
        projectId: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/archive-project\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Call SHC API
  async callShc(projectId: string) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/shc`, {
        projectId: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/shc\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Enhanced Pipeline Process API - unified endpoint for all pipeline components
  async processPipeline(projectId: string, components: any) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/pipeline/process`, {
        projectId: projectId,
        components: components
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/pipeline/process\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchDataSchedulerHistory(requestData: {
    destination_pilot_id: string;
    job_name: string;
    uuid: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destination_pilot_id: requestData.destination_pilot_id,
        job_name: requestData.job_name,
        uuid: requestData.uuid
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/data-scheduler-history?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/data-scheduler-history\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }


  // Fetch data scheduler history for a specific user (all job types)
  async fetchUserDataSchedulerHistory(requestData: {
    destination_pilot_id: string;
    uuid: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destination_pilot_id: requestData.destination_pilot_id,
        uuid: requestData.uuid
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/data-scheduler-history?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/data-scheduler-history\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchConsumptionData(requestData: {
    uuid: string;
    destinationBaseUrl: string;
    meterFuel?: string;
    destinationPilotId?: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        uuid: requestData.uuid,
        destinationBaseUrl: requestData.destinationBaseUrl,
        meterFuel: requestData.meterFuel || ''
      });
      
      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/consumption-data?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/consumption-data\n` + 
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchUserAttribute(requestData: {
    uuid?: string;
    destination_pilot_id: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destination_pilot_id: requestData.destination_pilot_id
      });

      if (requestData.uuid) {
        queryParams.append('uuid', requestData.uuid);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetch-user-attribute?${queryParams.toString()}`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/fetch-user-attribute\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

    async assignPhysicalCluster(requestData: {
      uuid: string;
      destination_pilot_id: string;
    }) {
      try {
        const queryParams = new URLSearchParams({
          uuid: requestData.uuid,
          destination_pilot_id: requestData.destination_pilot_id
        });
  
        const response = await this.axiosClient.post(`${this.toolUrl}/v1/assign-physical-cluster?${queryParams.toString()}`, {}, {
          headers: {
            'Accept': 'application/json'
          }
        });
  
        return response;
      } catch (error) {
        this.rc.error(this.rc.getName(this),
          `Error >> POST request to : /v1/assign-physical-cluster\n` +
          `\nresulted in error : \n`, error);
        return { error: ErrorUtils.getApiErrorInfo(error) };
      }
    }

  async fetchBBUserProgram(requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    destinationPilotId: string;
    utilityId: string;
    hasBBUserProgram: boolean;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userUUID: requestData.userUUID,
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        destinationPilotId: requestData.destinationPilotId,
        utilityId: requestData.utilityId,
        hasBBUserProgram: requestData.hasBBUserProgram.toString()
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/BBUserProgram?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error: any) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/fetchProdUserData/BBUserProgram\n` +
        `\nresulted in error : \n`, error);
      
      // Extract errorDetails from response data if available (for 400 errors)
      if (error?.response?.data?.errorDetails) {
        return { error: error.response.data.errorDetails };
      } else if (error?.response?.data?.message) {
        return { error: error.response.data.message };
      }
      
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateSecondaryEmail(requestData: {
    uuid: string;
    destination_pilot_id: string;
    secondary_email: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/update-secondary-email`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/update-secondary-email\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateNameEmail(requestData: {
    uuid: string;
    destination_pilot_id: string;
    first_name: string;
    last_name?: string;
    primary_email: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/update-name-email`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/update-name-email\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateAddressZipcode(requestData: {
    uuid: string;
    destination_pilot_id: string;
    address: string;
    zipcode: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/update-address-zipcode`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error: any) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/update-address-zipcode\n` +
        `\nresulted in error : \n`, error);

      if (error?.response?.data?.message) {
        return {
          error: error.response.data.message
        }
      }

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async rateTransition(requestData: {
    uuid: string;
    destination_pilot_id: string;
    rate_plan: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/rate-transition`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/rate-transition\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async syncWeatherData(requestData: {
    source_uuid: string[];
    source_environment: string;
    destinationEnvironment: string;
    destination_pilot_id: string;
    sourceCountry?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/fetchProdUserData/syncWeatherData`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/fetchProdUserData/syncWeatherData\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }
}